/**
 * Instructor Service
 *
 * Client-side service for the instructor referral program.
 * Handles instructor registration, profile management, and promo code tracking.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/lib/firebase';
import type { Instructor, CertifyingAgency } from '@/types/models';

// Firebase Functions
const functions = getFunctions(app);

// Types for API responses
export interface RegisterInstructorData {
  displayName: string;
  organization?: string;
  certifyingAgency?: CertifyingAgency;
  certifications?: string[];
  phone?: string;
  website?: string;
  referralReason?: string;
}

export interface RegisterInstructorResult {
  success: boolean;
  message: string;
  status: 'pending';
}

export interface InstructorProfileResult {
  exists: boolean;
  instructor: Instructor | null;
}

export interface PendingInstructorsResult {
  instructors: Instructor[];
  count: number;
}

export interface AllInstructorsResult {
  instructors: Instructor[];
  count: number;
}

export interface ApproveInstructorResult {
  success: boolean;
  message: string;
  promoCodesGenerated: number;
}

export interface RejectInstructorResult {
  success: boolean;
  message: string;
}

export interface IssueAdditionalCouponsResult {
  success: boolean;
  message: string;
  newCodesCount: number;
  totalCodes: number;
}

export interface AdminStatusResult {
  isAdmin: boolean;
}

/**
 * Register as an instructor
 *
 * Submits an instructor application for admin review.
 * Upon approval, the instructor receives free course promo codes.
 *
 * @param data - Instructor registration data
 * @returns Registration result
 */
export async function registerInstructor(
  data: RegisterInstructorData
): Promise<RegisterInstructorResult> {
  const registerInstructorFn = httpsCallable<
    RegisterInstructorData,
    RegisterInstructorResult
  >(functions, 'registerInstructor');

  const result = await registerInstructorFn(data);
  return result.data;
}

/**
 * Get instructor profile
 *
 * Retrieves the instructor profile for the current user or a specific instructor.
 * Admins can view any instructor; users can only view their own.
 *
 * @param instructorId - Optional instructor ID (defaults to current user)
 * @returns Instructor profile or null if not found
 */
export async function getInstructorProfile(
  instructorId?: string
): Promise<InstructorProfileResult> {
  const getInstructorProfileFn = httpsCallable<
    { instructorId?: string },
    InstructorProfileResult
  >(functions, 'getInstructorProfile');

  const result = await getInstructorProfileFn({ instructorId });
  return result.data;
}

/**
 * Get pending instructor applications (Admin only)
 *
 * @returns List of pending instructor applications
 */
export async function getPendingInstructors(): Promise<PendingInstructorsResult> {
  const getPendingInstructorsFn = httpsCallable<void, PendingInstructorsResult>(
    functions,
    'getPendingInstructors'
  );

  const result = await getPendingInstructorsFn();
  return result.data;
}

/**
 * Get all instructors (Admin only)
 *
 * @param status - Optional filter by status
 * @returns List of instructors
 */
export async function getAllInstructors(
  status?: 'pending' | 'approved' | 'suspended'
): Promise<AllInstructorsResult> {
  const getAllInstructorsFn = httpsCallable<
    { status?: string },
    AllInstructorsResult
  >(functions, 'getAllInstructors');

  const result = await getAllInstructorsFn({ status });
  return result.data;
}

/**
 * Approve an instructor application (Admin only)
 *
 * Generates Stripe promotion codes and notifies the instructor.
 *
 * @param instructorId - The instructor's user ID
 * @returns Approval result with promo code count
 */
export async function approveInstructor(
  instructorId: string
): Promise<ApproveInstructorResult> {
  const approveInstructorFn = httpsCallable<
    { instructorId: string },
    ApproveInstructorResult
  >(functions, 'approveInstructor');

  const result = await approveInstructorFn({ instructorId });
  return result.data;
}

/**
 * Reject an instructor application (Admin only)
 *
 * @param instructorId - The instructor's user ID
 * @param reason - Optional rejection reason
 * @returns Rejection result
 */
export async function rejectInstructor(
  instructorId: string,
  reason?: string
): Promise<RejectInstructorResult> {
  const rejectInstructorFn = httpsCallable<
    { instructorId: string; reason?: string },
    RejectInstructorResult
  >(functions, 'rejectInstructor');

  const result = await rejectInstructorFn({ instructorId, reason });
  return result.data;
}

/**
 * Issue additional coupons to an instructor (Admin only)
 *
 * @param instructorId - The instructor's user ID
 * @param count - Number of additional coupons (1-50, default 5)
 * @returns Result with new coupon count
 */
export async function issueAdditionalCoupons(
  instructorId: string,
  count: number = 5
): Promise<IssueAdditionalCouponsResult> {
  const issueAdditionalCouponsFn = httpsCallable<
    { instructorId: string; count: number },
    IssueAdditionalCouponsResult
  >(functions, 'issueAdditionalCoupons');

  const result = await issueAdditionalCouponsFn({ instructorId, count });
  return result.data;
}

/**
 * Check if current user is an admin
 *
 * @returns Admin status
 */
export async function checkAdminStatus(): Promise<boolean> {
  const checkAdminStatusFn = httpsCallable<void, AdminStatusResult>(
    functions,
    'checkAdminStatus'
  );

  const result = await checkAdminStatusFn();
  return result.data.isAdmin;
}

/**
 * Certifying agencies for dropdown selection
 */
export const CERTIFYING_AGENCIES: { value: CertifyingAgency; label: string }[] = [
  { value: 'PADI', label: 'PADI (Professional Association of Diving Instructors)' },
  { value: 'SSI', label: 'SSI (Scuba Schools International)' },
  { value: 'TDI', label: 'TDI (Technical Diving International)' },
  { value: 'SDI', label: 'SDI (Scuba Diving International)' },
  { value: 'NAUI', label: 'NAUI (National Association of Underwater Instructors)' },
  { value: 'IANTD', label: 'IANTD (International Association of Nitrox and Technical Divers)' },
  { value: 'Other', label: 'Other' },
];
